import clsx from 'clsx'
import includes from 'lodash/includes'
import map from 'lodash/map'
import React, { CSSProperties, FC, useCallback, useContext, useEffect, useRef } from 'react'

import SVGClose from '@Assets/SVG/Close'
import SvgZoomIn from '@Assets/SVG/ZoomIn'
import { ProductContext } from '@Components/Product/Context'
import ArrowPagination from '@Components/Product/Slider/Components/ArrowPagination/ArrowPagination'
import { ImageHorizontal } from '@Components/Product/Slider/Components/Image/ImageHorizontal'
import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { DragNext } from '@Components/Product/Slider/gestures/dragNext'
import { VideoHorizontal } from '@Components/Product/Slider/Video'
import { VimeoHorizontal } from '@Components/Product/Slider/VideoVimeo'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './Horizontal.module.scss'

const SWIPE_UP_ANIMATION_DURATION = 500

interface HorizontalSliderProps {
  autoHeight?: boolean
  children?: React.ReactNode
  className?: string
  showStarRating?: boolean
  zoomOnDoubleClick?: boolean
}

const HorizontalSlider: FC<HorizontalSliderProps> = ({ autoHeight, children, className, showStarRating = true, zoomOnDoubleClick }) => {
  const { t } = useTranslation()
  const productVideoAutoplay = useSplitTest('product_video_autoplay_v1', 'b')

  const {
    disableDrag,
    isDragDisabled,
    isZoomedIn,
    setZoomedIn,
  } = useContext(ControllerContext)
  const {
    activeSlide,
    goToNextSlide,
    goToPreviousSlide,
    isFullscreen,
    isSwipedUp,
    isVideoSlide,
    setSwipeUp,
    slides,
    toggleFullscreen,
  } = useContext(SliderContext)

  const product = useContext(ProductContext)

  const horizontalRef = useRef<HTMLUListElement>(null)
  DragNext(horizontalRef, goToNextSlide, goToPreviousSlide, activeSlide.key, 'x')

  const updateToggles = useCallback(() => {
    if (isSwipedUp) {
      const html = (document.getElementsByTagName('BODY') as HTMLCollectionOf<HTMLElement>)[0]
      html.style.overflow = ''
      html.style.position = ''

      setTimeout(() => {
        setSwipeUp(false)
        disableDrag(false)
        toggleFullscreen()
      }, SWIPE_UP_ANIMATION_DURATION)
    }
  }, [disableDrag, isSwipedUp, setSwipeUp, toggleFullscreen])

  useEffect(() => {
    updateToggles()
  }, [isSwipedUp, updateToggles])

  useEffect(() => {
    const onKeyUp = (e) => {
      if (e.key === 'ArrowRight' || e.keyCode === 39) {
        goToNextSlide(true)
      }
      if (e.key === 'ArrowLeft' || e.keyCode === 37) {
        goToPreviousSlide(true)
      }
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        toggleFullscreen()
      }
    }
    if (isFullscreen) {
      window.addEventListener('keyup', onKeyUp)
      return () => window.removeEventListener('keyup', onKeyUp)
    }
    return () => window.removeEventListener('keyup', onKeyUp)
  }, [goToNextSlide, goToPreviousSlide, isFullscreen, toggleFullscreen])

  const touchAction = isFullscreen ? 'none' : 'pan-y'

  const handleDoubleClick = () => {
    if (!isFullscreen && zoomOnDoubleClick) {
      toggleFullscreen()
    }
  }

  const content = ref => (
    <>
      {children}

      {!isDragDisabled && (
        <ArrowPagination
          onClickLeft={() => goToPreviousSlide(true)}
          onClickRight={() => goToNextSlide(true)}
        />
      )}
      <span style={{ paddingTop: '100%', display: 'block' }} />
      <ul
        ref={slides.length === 1 ? undefined : ref}
        style={{
          display: 'flex',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          listStyle: 'none',
          padding: 0,
          margin: 0,
          touchAction,
          transform: `translateX(-${activeSlide.key * 100}%)`,
          '-webkit-touch-action': touchAction,
        } as CSSProperties}
      >
        {map(slides, (slide, key) => (
          <li
            key={`${slide.url}-${key}`}
            onDoubleClick={handleDoubleClick}
            style={{
              display: 'flex',
              flexShrink: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
          >
            {
              // eslint-disable-next-line no-nested-ternary
              includes(slide.tags, 'video')
                ? includes(slide.tags, 'vimeo')
                  ? (
                    <VimeoHorizontal
                      key={key}
                      asset={slide}
                      isActive={activeSlide.key === key}
                      isFullscreen={isFullscreen}
                      setting={productVideoAutoplay ? { autoplay: activeSlide.key === key } : undefined}
                    />
                  )
                  : (
                    <VideoHorizontal
                      asset={slide}
                      isActive={activeSlide.key === key}
                      isFullscreen={false}
                    />
                  )
                : <ImageHorizontal asset={slide} index={key} title={product?.name || ''} />
            }
          </li>
        ))}
      </ul>
      {isZoomedIn
        && <button className={styles.exitZoom} onClick={() => setZoomedIn(false)} type="button">{t('common:ZOOM_OUT')}</button>}
      {!isSwipedUp && isFullscreen && (
        <button className={styles.closeButton} onClick={toggleFullscreen} type="button">
          {' '}
          <SVGClose />
        </button>
      )}
    </>
  )
  const zoomButton = (isSwipedUp || !isFullscreen) && !isVideoSlide && !zoomOnDoubleClick && (
    <div className={clsx(styles.zoomWrapper, showStarRating && styles.bottom)}>
      <button
        aria-label={t('GALLERY_ZOOM_LABEL')}
        className={styles.zoomInButton}
        onClick={toggleFullscreen}
        type="button"
      >
        <SvgZoomIn />
      </button>
    </div>
  )

  return (
    <>
      <div
        className={clsx(
          className,
          styles.horizontalSliderRoot,
          isFullscreen && styles.fullscreen,
          isSwipedUp && styles.swipe,
          autoHeight && styles.autoHeight,
        )}
      >
        {content(horizontalRef)}
        {zoomButton}
      </div>
      {isSwipedUp && (
        <div
          className={clsx(className, styles.horizontalSliderRoot)}
        >
          {content(null)}
          {zoomButton}
        </div>
      )}
    </>
  )
}

export default HorizontalSlider
